import { useEventInfo } from './useEventInfo';

/**
 * Was this event built to run on the Firebase-free stack?
 *
 * This is the *product* question, and it is deliberately not the same as "does this event have a
 * Firebase document?". The two agree for every event that exists today and diverge for exactly one
 * case — a wedding created on the new stack, which is `eventType: wedding` with no Firebase
 * document behind it. Anything choosing a data source or a screen for a wedding wants this one;
 * anything asking whether a Firebase read will find something wants the synthetic-id check instead.
 *
 * One accessor rather than each caller reading the field, because every caller has to agree: a
 * wedding routed to the new-stack guest list but the legacy schedule editor is worse than one
 * routed consistently either way.
 *
 * Live since 2026-08-31: `usesNewStack` is in the production schema (event_service #2747 deployed),
 * the basic-event-info fragment selects it, and the provider maps it through. Every event created
 * before the new stack existed resolves `false`, which is also correct for all of them.
 */
/**
 * Deliberately NOT re-exported from `@shared/utils/eventInfo`, and imported from this path instead.
 *
 * Many specs replace that barrel with a factory naming only `useEventInfo`. A hook reached through
 * the barrel therefore arrives `undefined` and throws at the call site, and the failure surfaces one
 * CI shard at a time rather than all at once. Imported from here it is out of the mock's reach: the
 * real hook runs, `useEventInfo` resolves through its own module, and the context is non-strict, so
 * outside a provider this returns `false` — which is what every existing spec already expects.
 */
export const useIsNewStackEvent = (): boolean => {
  const { eventInfo } = useEventInfo();

  return eventInfo?.usesNewStack === true;
};
